import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { EstadoRedes, EstadoRedesContexto, EstadoRedesResponse } from '@/types/api';

// GET /api/obras/:cc/estado-redes — contexto del diálogo «Estado de redes»
// (design_estado_redes_obra.md §4): estado actual, conteo de redes abiertas/
// archivadas e historial (obra_eventos). Query propia, separada de
// ['obra', cc] (el header, staleTime 60s) y de ['obras'] (la home, staleTime
// 5 min): el diálogo se abre justo para ver el estado más reciente y su
// historial completo — staleTime 0, siempre refetchea al abrir.
export function useEstadoRedes(cc: string | null) {
  return useQuery({
    queryKey: ['obra', cc, 'estado-redes'],
    queryFn: () => api<EstadoRedesContexto>(`/api/obras/${encodeURIComponent(cc!)}/estado-redes`),
    enabled: cc !== null,
    staleTime: 0,
  });
}

export interface CambiarEstadoRedesInput {
  cc: string;
  estado: EstadoRedes;
  nota?: string | null;
}

// POST /api/obras/:cc/estado-redes — mismo patrón de mutation que
// use-rdt-mutations.ts, pero con invalidación propia (esta mutación es de
// obra, no de rdt: no encaja en el `invalidarRdt` compartido de ese
// archivo). Invalida ['obras'] (la home), ['obra', cc] (el header del
// detalle — TanStack Query matchea por prefijo, así que también alcanza a
// ['obra', cc, 'estado-redes'], el contexto del diálogo) y ['avivador']
// (build Avivador, 21/9/2026: la rama R4 — Estado.Redes vacío — depende
// exactamente de este campo, y Archivado/No Aplica sacan la obra entera del
// Avivador, design_avivador.md §10.4).
export function useCambiarEstadoRedes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cc, ...body }: CambiarEstadoRedesInput) =>
      api<EstadoRedesResponse>(`/api/obras/${encodeURIComponent(cc)}/estado-redes`, { method: 'POST', body }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['obras'] });
      void queryClient.invalidateQueries({ queryKey: ['obra', variables.cc] });
      void queryClient.invalidateQueries({ queryKey: ['avivador'] });
    },
  });
}
